import * as React from 'react';
import { useCallback } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { cn } from '../lib/utils';

export interface PersonProfileValues {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  title: string;
  location: string;
  imageName: string;
  imageType: string;
  avatarFile: string;
  initials: string;
  functionalRoles: string;
}

interface PersonProfileCardProps {
  mode: 'edit' | 'view';
  values: PersonProfileValues;
  onValuesChange?: (values: PersonProfileValues) => void;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  title?: string;
  teams?: { id: string; name: string }[];
  onAddTeams?: () => void;
  showTeamSelector?: boolean;
  className?: string;
}

export function PersonProfileCard({
  mode,
  values,
  onValuesChange,
  onSave,
  onCancel,
  isSaving = false,
  title = 'New Profile',
  teams = [],
  onAddTeams,
  showTeamSelector = false,
  className,
}: PersonProfileCardProps) {
  const updateField = useCallback(
    (field: keyof PersonProfileValues, value: string) => {
      if (onValuesChange) {
        onValuesChange({ ...values, [field]: value });
      }
    },
    [values, onValuesChange]
  );

  if (mode === 'view') {
    return <PersonProfileViewCard values={values} className={className} />;
  }

  return (
    <div
      className={cn(
        'bg-background border border-border rounded-2xl shadow-xl w-full max-w-[520px] flex flex-col',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border">
        <h2 className="text-lg font-bold text-foreground">{title}</h2>
        {onCancel && (
          <button
            onClick={onCancel}
            className="size-6 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="size-5" />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 pt-8 pb-6">
        <div className="flex gap-8">
          {/* Form Fields */}
          <div className="flex-1 min-w-0 space-y-6">
            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-base font-bold text-foreground">
                Contact Information
              </h3>

              <div className="space-y-3">
                {/* Name */}
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Name</label>
                  <Input
                    value={values.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="Enter name"
                    className="h-[42px] rounded-xl border-border/60"
                  />
                </div>

                {/* Title */}
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Title</label>
                  <Input
                    value={values.title}
                    onChange={(e) => updateField('title', e.target.value)}
                    placeholder="Enter title"
                    className="h-[42px] rounded-xl border-border/60"
                  />
                </div>

                {/* Email + Phone side by side */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">
                      Email
                    </label>
                    <Input
                      type="email"
                      value={values.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="email@example.com"
                      className="h-[42px] rounded-xl border-border/60"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">
                      Phone number
                    </label>
                    <Input
                      type="tel"
                      value={values.phoneNumber}
                      onChange={(e) =>
                        updateField('phoneNumber', e.target.value)
                      }
                      placeholder="(555) 123-4567"
                      className="h-[42px] rounded-xl border-border/60"
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">
                    Address
                  </label>
                  <Input
                    value={values.location}
                    onChange={(e) => updateField('location', e.target.value)}
                    placeholder="Enter address"
                    className="h-[42px] rounded-xl border-border/60"
                  />
                </div>
              </div>
            </div>

            {/* Team Section */}
            {showTeamSelector && (
              <div className="space-y-4">
                <h3 className="text-base font-bold text-foreground">Team</h3>
                <button
                  onClick={onAddTeams}
                  className="w-full h-12 rounded-xl border border-border/60 px-4 flex items-center justify-between text-base font-medium text-foreground hover:bg-muted/50 transition-colors"
                >
                  <span>
                    {teams.length > 0
                      ? `${teams.length} team${
                          teams.length > 1 ? 's' : ''
                        } selected`
                      : 'Add to teams'}
                  </span>
                  <svg
                    className="size-5 text-muted-foreground"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="flex items-center justify-end gap-4 px-6 pb-8">
        {onCancel && (
          <button
            onClick={onCancel}
            className="flex-1 max-w-[160px] h-12 rounded-full border border-border text-base font-bold text-foreground hover:bg-muted transition-colors"
          >
            Cancel
          </button>
        )}
        {onSave && (
          <button
            onClick={onSave}
            disabled={isSaving}
            className="flex-1 max-w-[160px] h-12 rounded-full bg-[hsl(var(--primary-dark))] text-base font-bold text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {isSaving ? (
              <Loader2 className="size-5 animate-spin mx-auto" />
            ) : (
              'Save changes'
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// ── View Mode Card (used on detail page) ────────────────────

function PersonProfileViewCard({
  values,
  className,
}: {
  values: PersonProfileValues;
  className?: string;
}) {
  const fields = [
    { label: 'Email', value: values.email },
    { label: 'Phone', value: values.phoneNumber },
    { label: 'Title', value: values.title },
    { label: 'Location', value: values.location || 'None' },
  ];

  return (
    <div
      className={cn(
        'bg-background border border-border/60 rounded-xl p-6',
        className
      )}
    >
      <h3 className="text-base font-bold text-foreground mb-6">
        Contact Details
      </h3>
      <div className="space-y-3">
        {fields.map((field) => (
          <div key={field.label} className="border-b border-border pb-3">
            <p className="text-sm font-medium text-muted-foreground mb-1">
              {field.label}
            </p>
            <p className="text-base font-medium text-foreground">
              {field.value || 'None'}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
